import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  TrophyIcon,
  StarIcon,
  SparklesIcon,
  ArrowRightIcon,
  ChartBarIcon,
  FireIcon,
} from '@heroicons/react/24/outline'
import { TrophyIcon as TrophySolid } from '@heroicons/react/24/solid'
import { Card, CardHeader } from '@/components/ui'
import { gamificationApi } from '@/api'
import type { Badge as GamificationBadge, UserLevel } from '@/api/gamification'

interface Props {
  points?: number
  level?: number
  badgesCount?: number
  rank?: number | null
}

export default function GamificationWidget({
  points: initialPoints = 0,
  level: initialLevel = 1,
  badgesCount: initialBadgesCount = 0,
  rank: initialRank
}: Props) {
  const [stats, setStats] = useState({
    points: initialPoints,
    level: initialLevel,
    badgesCount: initialBadgesCount,
    rank: initialRank,
  })
  const [levelInfo, setLevelInfo] = useState<UserLevel | null>(null)
  const [recentBadges, setRecentBadges] = useState<GamificationBadge[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchGamificationData()
  }, [])

  const fetchGamificationData = async () => {
    try {
      const data = await gamificationApi.getMyStats()
      setStats({
        points: data.level?.total_points || initialPoints,
        level: data.level?.level || initialLevel,
        badgesCount: data.badges?.length || initialBadgesCount,
        rank: data.rank || initialRank,
      })
      setLevelInfo(data.level)
      setRecentBadges(data.badges?.slice(0, 4) || [])
    } catch (error) {
      console.error('Error fetching gamification data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getLevelColor = (level: number) => {
    if (level >= 10) return 'from-yellow-400 to-amber-500'
    if (level >= 7) return 'from-purple-400 to-indigo-500'
    if (level >= 4) return 'from-blue-400 to-cyan-500'
    return 'from-green-400 to-emerald-500'
  }

  const getLevelTitle = (level: number) => {
    if (level >= 10) return 'Esperto'
    if (level >= 7) return 'Avanzato'
    if (level >= 4) return 'Intermedio'
    return 'Principiante'
  }

  if (loading) {
    return (
      <Card>
        <CardHeader title="Gamification" />
        <div className="animate-pulse space-y-4">
          <div className="h-24 bg-gray-100 rounded-lg" />
          <div className="h-16 bg-gray-100 rounded-lg" />
        </div>
      </Card>
    )
  }

  const progressPercentage = levelInfo?.progress_percentage || 0

  return (
    <Card className="h-full">
      <CardHeader
        title={
          <div className="flex items-center gap-2">
            <TrophyIcon className="h-5 w-5 text-yellow-500" />
            <span>Gamification</span>
          </div>
        }
        action={
          <Link
            to="/leaderboard"
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
          >
            Classifica <ArrowRightIcon className="h-4 w-4 ml-1" />
          </Link>
        }
      />

      {/* Level Card */}
      <div className={`p-4 rounded-xl bg-gradient-to-r ${getLevelColor(stats.level)} text-white mb-4`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-90">Livello {stats.level}</p>
            <p className="text-2xl font-bold">{getLevelTitle(stats.level)}</p>
          </div>
          <div className="text-right">
            <SparklesIcon className="h-10 w-10 opacity-80" />
          </div>
        </div>

        {/* Level progress */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="opacity-90">Progressione</span>
            <span className="font-medium">{progressPercentage}%</span>
          </div>
          <div className="h-2 bg-white/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          {levelInfo && (
            <p className="text-xs opacity-80 mt-1">
              {levelInfo.current_points} / {levelInfo.next_level_points} punti per il prossimo livello
            </p>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-3 bg-yellow-50 rounded-lg">
          <StarIcon className="h-6 w-6 mx-auto text-yellow-500 mb-1" />
          <p className="text-lg font-bold text-gray-900">{stats.points}</p>
          <p className="text-xs text-gray-500">Punti</p>
        </div>
        <div className="text-center p-3 bg-purple-50 rounded-lg">
          <TrophySolid className="h-6 w-6 mx-auto text-purple-500 mb-1" />
          <p className="text-lg font-bold text-gray-900">{stats.badgesCount}</p>
          <p className="text-xs text-gray-500">Badge</p>
        </div>
        <div className="text-center p-3 bg-blue-50 rounded-lg">
          <ChartBarIcon className="h-6 w-6 mx-auto text-blue-500 mb-1" />
          <p className="text-lg font-bold text-gray-900">
            {stats.rank ? `#${stats.rank}` : '-'}
          </p>
          <p className="text-xs text-gray-500">Posizione</p>
        </div>
      </div>

      {/* Recent Badges */}
      {recentBadges.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <FireIcon className="h-4 w-4 text-orange-500" />
            Badge recenti
          </p>
          <div className="flex flex-wrap gap-2">
            {recentBadges.map((badge) => (
              <div
                key={badge.id}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full border border-gray-200"
                title={badge.description}
              >
                {badge.icon ? (
                  <img src={badge.icon} alt={badge.name} className="w-5 h-5 object-contain" />
                ) : (
                  <TrophyIcon className="w-5 h-5 text-yellow-500" />
                )}
                <span className="text-xs font-medium text-gray-700">{badge.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link to badges page */}
      <div className="mt-4 pt-3 border-t">
        <Link
          to="/profile/badges"
          className="text-sm text-primary-600 hover:text-primary-700 flex items-center justify-center gap-1"
        >
          Vedi tutti i badge
          <ArrowRightIcon className="h-4 w-4" />
        </Link>
      </div>
    </Card>
  )
}
